import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Card, message, Typography, Alert, Tag,
} from 'antd';
import { PlusOutlined, DeleteOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useSidebar } from '../../contexts/SidebarContext';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../../components/shared/PageHeader';
import { useRolePreview } from '../../contexts/RolePreviewContext';
import {
  listWorkspaceSessions, createWorkspaceSession, deleteWorkspaceSession, startWorkspaceSession,
  openWorkspaceSessionDesktop, isWorkspaceSessionRunning,
  listGroupInstances, getGroupInstance, listIwmProfiles,
  WorkspaceSession, GroupInstance, GroupMember, IwmProfile,
} from '../../services/workspaceApi';
import { formatIwmProxyError } from '../../services/iwmError';
import { fetchAllUsers } from '../../services/api';

const isIwmConfigError = (e: unknown): string | null => {
  const err = e as {
    response?: { status?: number; data?: { message?: string; code?: string } };
  };
  const status = err?.response?.status;
  const msg = err?.response?.data?.message || '';
  const code = err?.response?.data?.code || '';
  // Business / catalog errors from a reachable IWM — not config failures
  if (
    code === 'PROFILE_NOT_FOUND' ||
    code === 'HOME_NOT_MOUNTABLE' ||
    code === 'CAPACITY_EXCEEDED' ||
    /unknown profile|HOME_NOT_MOUNTABLE|does not know this profile/i.test(msg)
  ) {
    return null;
  }
  if (
    status === 503 ||
    /IWM_API_(BASE|TOKEN) is not configured|not configured — set|unreachable|ECONNREFUSED/i.test(msg)
  ) {
    return msg || 'Infra Workspace Manager (IWM) is not configured or unreachable.';
  }
  return null;
};

const errMsg = (e: unknown, fallback: string) => {
  const err = e as { errorFields?: unknown; response?: { data?: { message?: string } } };
  if (err?.errorFields) return null;
  return err?.response?.data?.message || fallback;
};

const CHIP_BASE = 'inline-block px-2 py-0.5 text-xs font-medium rounded border';

function toneChipClass(tone: string): string {
  switch (tone) {
    case 'success':
      return 'bg-success/15 text-success border-success';
    case 'error':
      return 'bg-danger/15 text-danger border-danger';
    case 'warning':
      return 'bg-warning/15 text-warning border-warning';
    case 'info':
      return 'bg-info/15 text-info border-info';
    default:
      return 'bg-accent/15 text-accent border-accent';
  }
}

function TokenChip({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span className={`${CHIP_BASE} ${toneChipClass(tone)}`}>
      {children}
    </span>
  );
}

function sessionStatusVisual(status?: string): { color: string; tone: string; label: string } {
  const s = String(status || '').toLowerCase();
  const label = status || '—';
  if (s === 'running') return { color: 'green', tone: 'success', label };
  if (s === 'provisioning' || s === 'requested' || s === 'planned') return { color: 'blue', tone: 'info', label };
  if (s === 'failed' || s === 'error') return { color: 'red', tone: 'error', label };
  if (s === 'expired') return { color: 'orange', tone: 'warning', label };
  if (s === 'deleted' || s === 'deleting') return { color: 'default', tone: 'accent', label };
  return { color: 'default', tone: 'accent', label };
}

function StatusTag({ status }: { status?: string }) {
  const useNew = useNewGui();
  const v = sessionStatusVisual(status);
  if (useNew) {
    return <TokenChip tone={v.tone}>{v.label}</TokenChip>;
  }
  return <Tag color={v.color}>{v.label}</Tag>;
}

const isSessionGone = (status?: string) => {
  const s = String(status || '').toLowerCase();
  return s === 'deleted' || s === 'deleting';
};

type UniquePick = { userId: string; email?: string };

const WorkspaceSessionsPage: React.FC = () => {
  const { isSidebarOpen } = useSidebar();
  const useNew = useNewGui();
  const { canWriteWorkspace: canWrite } = useRolePreview();

  const [rows, setRows] = useState<WorkspaceSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [iwmError, setIwmError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [groups, setGroups] = useState<GroupInstance[]>([]);
  const [profiles, setProfiles] = useState<IwmProfile[]>([]);
  const [uniqueUsers, setUniqueUsers] = useState<UniquePick[]>([]);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [form] = Form.useForm();
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startForm] = Form.useForm();
  const [startError, setStartError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const selectedGroupId = Form.useWatch('groupInstanceId', form);

  const load = useCallback(async () => {
    setLoading(true);
    setIwmError(null);
    try {
      const data = await listWorkspaceSessions();
      const list = Array.isArray(data)
        ? data
        : data && typeof data === 'object' && Array.isArray((data as { sessions?: WorkspaceSession[] }).sessions)
          ? (data as { sessions: WorkspaceSession[] }).sessions
          : [];
      setRows(
        list.map((s) => ({
          ...s,
          profileId: s.profileId || (s.profile as string | undefined) || '',
        })),
      );
    } catch (e) {
      const iwm = isIwmConfigError(e);
      if (iwm) {
        setIwmError(iwm);
        setRows([]);
      } else {
        message.error(errMsg(e, 'Failed to load sessions') || 'Failed to load sessions');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLookups = useCallback(async () => {
    setLookupsLoading(true);
    try {
      const [g, p, u] = await Promise.all([
        listGroupInstances().catch(() => [] as GroupInstance[]),
        listIwmProfiles().catch(() => [] as IwmProfile[]),
        fetchAllUsers().catch(() => [] as UniquePick[]),
      ]);
      setGroups(Array.isArray(g) ? g : []);
      setProfiles(Array.isArray(p) ? p : []);
      setUniqueUsers(
        (Array.isArray(u) ? u : [])
          .map((row: any) => ({
            userId: String(row.userId || row.userid || row.loginId || '').trim(),
            email: row.email || row.emailId || undefined,
          }))
          .filter((row) => row.userId),
      );
    } finally {
      setLookupsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!modalOpen) return;
    loadLookups();
  }, [modalOpen, loadLookups]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedGroupId) {
        setGroupMembers([]);
        return;
      }
      try {
        const full = await getGroupInstance(Number(selectedGroupId));
        if (!cancelled) setGroupMembers(full.members || []);
      } catch {
        if (!cancelled) setGroupMembers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedGroupId]);

  const userOptions = useMemo(() => {
    const fromMembers = groupMembers
      .filter((m) => m.userId)
      .map((m) => ({
        value: m.userId,
        label: m.email ? `${m.userId} — ${m.email} (member)` : `${m.userId} (member)`,
      }));
    const memberIds = new Set(fromMembers.map((o) => o.value));
    const fromUnique = uniqueUsers
      .filter((u) => !memberIds.has(u.userId))
      .map((u) => ({
        value: u.userId,
        label: u.email ? `${u.userId} — ${u.email}` : u.userId,
      }));
    // Prefer group members at top when a group is selected
    return [...fromMembers, ...fromUnique];
  }, [groupMembers, uniqueUsers]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setCreateError(null);
      const payload: { user: string; profileId?: string; groupInstanceId?: number } = {
        user: String(values.user).trim(),
      };
      if (values.groupInstanceId != null && values.groupInstanceId !== '') {
        payload.groupInstanceId = Number(values.groupInstanceId);
      } else if (values.profileId) {
        payload.profileId = String(values.profileId).trim();
      }
      await createWorkspaceSession(payload);
      message.success('Session started');
      setModalOpen(false);
      form.resetFields();
      await load();
    } catch (e) {
      if ((e as { errorFields?: unknown })?.errorFields) return;
      const iwm = isIwmConfigError(e);
      const detail = formatIwmProxyError(e, 'Start failed');
      setCreateError(detail);
      if (iwm) setIwmError(iwm);
      if (detail) message.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleStart = async () => {
    try {
      const values = await startForm.validateFields();
      setStarting(true);
      setStartError(null);
      await startWorkspaceSession(values.userId.trim());
      message.success('Session start requested');
      setStartModalOpen(false);
      startForm.resetFields();
      await load();
    } catch (e) {
      if ((e as { errorFields?: unknown })?.errorFields) return;
      const iwm = isIwmConfigError(e);
      const detail = formatIwmProxyError(e, 'Start failed');
      setStartError(detail);
      if (iwm) setIwmError(iwm);
      if (detail) message.error(detail);
    } finally {
      setStarting(false);
    }
  };

  const handleDelete = (record: WorkspaceSession) => {
    Modal.confirm({
      title: 'Delete session?',
      content: `Stop and remove session for ${record.user} (${record.id})?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteWorkspaceSession(record.id);
          message.success('Session deleted');
          await load();
        } catch (e) {
          const iwm = isIwmConfigError(e);
          message.error(iwm || errMsg(e, 'Delete failed') || 'Delete failed');
        }
      },
    });
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 180, ellipsis: true },
    { title: 'User', dataIndex: 'user', key: 'user', width: 140 },
    {
      title: 'Profile',
      key: 'profileId',
      width: 200,
      ellipsis: true,
      // IWM list items use `profile`, not `profileId` (session-spec-v1 §5).
      render: (_: unknown, record: WorkspaceSession) =>
        (record.profileId || (record.profile as string | undefined) || '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string | undefined) => <StatusTag status={status} />,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: 'noVNC',
      key: 'novnc',
      width: 80,
      render: (_: unknown, record: WorkspaceSession) =>
        isWorkspaceSessionRunning(record.status) ? (
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.preventDefault();
              openWorkspaceSessionDesktop(record.id).catch(() => {
                message.error('Failed to open desktop');
              });
            }}
          >
            Open
          </Button>
        ) : (
          record.novncUrl || '—'
        ),
    },
    canWrite
      ? {
          title: 'Actions',
          key: 'actions',
          width: 100,
          render: (_: unknown, record: WorkspaceSession) => (
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
              disabled={isSessionGone(record.status)}
              onClick={() => handleDelete(record)}
            >
              Delete
            </Button>
          ),
        }
      : null,
  ].filter(Boolean);

  return (
    <div className={`p-4 ${isSidebarOpen ? 'ml-64' : 'ml-16'} transition-all`}>
      <Card className="shadow">
        {useNew ? (
          <PageHeader
            title="Workspace Sessions"
            purpose="Operator view of IWM workspace sessions — create and tear down pods."
            actions={
              canWrite ? (
                <>
                  <Button
                    icon={<PlayCircleOutlined />}
                    onClick={() => { startForm.resetFields(); setStartError(null); setStartModalOpen(true); }}
                    disabled={!!iwmError}
                    style={{ marginRight: 8 }}
                  >
                    Start
                  </Button>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setCreateError(null); setModalOpen(true); }} disabled={!!iwmError}>
                    Create Session
                  </Button>
                </>
              ) : undefined
            }
          />
        ) : (
          <div className="flex justify-between items-center mb-4">
            <Typography.Title level={4} className="!mb-0">
              Workspace Sessions
            </Typography.Title>
            {canWrite && (
              <span>
                <Button
                  icon={<PlayCircleOutlined />}
                  onClick={() => { startForm.resetFields(); setStartError(null); setStartModalOpen(true); }}
                  disabled={!!iwmError}
                  style={{ marginRight: 8 }}
                >
                  Start
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setCreateError(null); setModalOpen(true); }} disabled={!!iwmError}>
                  Create Session
                </Button>
              </span>
            )}
          </div>
        )}

        {iwmError && (
          <Alert
            type="warning"
            showIcon
            className="mb-4"
            message="IWM not configured"
            description={iwmError}
            action={
              <Button size="small" onClick={load}>
                Retry
              </Button>
            }
          />
        )}

        <Table
          rowKey="id"
          size="small"
          loading={loading}
          columns={columns as any}
          dataSource={rows}
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title="Start Workspace Session (IC-E1)"
        open={modalOpen}
        onOk={handleCreate}
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        okText="Start"
        destroyOnClose
        width={560}
      >
        {createError && (
          <Alert type="error" showIcon className="mb-4" message={createError} />
        )}
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message="Pick a Group Instance (preferred) — IC resolves GroupPolicy. Profile is only for operator override when no group is selected."
        />
        <Form form={form} layout="vertical">
          <Form.Item name="groupInstanceId" label="Group Instance (preferred)">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={lookupsLoading}
              placeholder="Select from Groups"
              options={groups.map((g) => ({
                value: g.id,
                label: `#${g.id} — ${g.name || g.profileId || 'unnamed'}${g.status ? ` [${g.status}]` : ''}`,
              }))}
              onChange={() => {
                // Clear user when group changes so member list drives pick
                form.setFieldsValue({ user: undefined });
              }}
            />
          </Form.Item>
          <Form.Item name="user" label="User" rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              loading={lookupsLoading}
              placeholder={
                selectedGroupId
                  ? 'Pick a group member (or any Unique User)'
                  : 'Pick from Unique Users'
              }
              options={userOptions}
              notFoundContent={lookupsLoading ? 'Loading…' : 'No users found'}
            />
          </Form.Item>
          <Form.Item
            name="profileId"
            label="Profile ID (override if no group)"
            extra="Only needed when Group Instance is empty — lists live IWM profiles."
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              loading={lookupsLoading}
              disabled={Boolean(selectedGroupId)}
              placeholder="Select IWM profile"
              options={profiles.map((p) => ({
                value: p.id,
                label: p.displayName && p.displayName !== p.id ? `${p.id} — ${p.displayName}` : p.id,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Start Workspace Session"
        open={startModalOpen}
        onOk={handleStart}
        confirmLoading={starting}
        onCancel={() => setStartModalOpen(false)}
        okText="Start"
        destroyOnClose
      >
        {startError && (
          <Alert type="error" showIcon className="mb-4" message={startError} />
        )}
        <Form form={startForm} layout="vertical">
          <Form.Item name="userId" label="User" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="IPA / POSIX username — must have an active group and a ready NFS home" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WorkspaceSessionsPage;
